import {combineLatest as observableCombineLatest, from, merge, Observable, of as observableOf, ReplaySubject, Subject, Subscription} from 'rxjs';

import {concatMap, distinctUntilChanged, filter, ignoreElements, last, map, mergeMap, switchMap, take, tap, timeout} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Device, deviceFromBackendObject, DevToolsEndpoint} from '../../types/devices/device';
import {DeviceId, DeviceSlotId, ProjectId} from '../../types/ids';
import {EventsService, WebmateWebsocketEvent} from '../events/events.service';
import {isDefined, isNoUUID, isUUID, valuesOfObject} from '../../utils/utils';
import {UserService} from '../user.service';
import {DeviceApi} from '../../api/rest/device.restapi';
import {
    createFinishedPackageInstallation,
    VirtualPackage
} from '@webmate-web-core/shared/types/packages/package';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {
    DeviceProperties,
    DevicePropertyNames,
    DeviceRequirements
} from '@webmate-web-core/shared/types/devices/device-properties';
import {MobileAccessToken, MobileAccessTokenId} from '@webmate-web-core/user-profile/user-profile-mobileaccess.component';
import {GenericApiResponse} from '@webmate-web-core/shared/api/apibase.api';
import {DateTime} from '@webmate-web-core/shared/types/base-types';

export interface DevicePropertyRequirementUpdate {
    propertyName: string;
    value: any;
}

/** what the Device reports back after an App installation was requested, derived from the 'Device.AppInstalled'/'Device.AppInstallFailed' Events */
export interface AppInstallationResult {
    deviceId: DeviceId;
    projectId: ProjectId;
    /** only set if the installation failed */
    errorMessage?: string;
}

@Injectable({
    providedIn: 'root'
})
export class DeviceService {

    /** how long we wait for a Device to report the outcome of an App installation before we give up and tell the user */
    private static readonly APP_INSTALLATION_TIMEOUT_MS = 60000;

    //  _____ _   _ _______ ______ _____  _   _          _
    // |_   _| \ | |__   __|  ____|  __ \| \ | |   /\   | |
    //   | | |  \| |  | |  | |__  | |__) |  \| |  /  \  | |
    //   | | | . ` |  | |  |  __| |  _  /| . ` | / /\ \ | |
    //  _| |_| |\  |  | |  | |____| | \ \| |\  |/ ____ \| |____
    // |_____|_| \_|  |_|  |______|_|  \_\_| \_/_/    \_\______|


    private _devicesById$: {[s: string /* DeviceId*/]: ReplaySubject<Device>}; //cache for Devices, the "main one"
    private _allDeviceIds: ReplaySubject< DeviceId[] >; //cache for signaling presence of new or removal of Devices
    private _deviceEventsHandleByProject: { [s: string /* ProjectId*/]: Observable<WebmateWebsocketEvent> } = {}; //events
    public _appInstalledEvents: Subject<any> = new Subject<any>();
    public _deviceRevertedToInitialEvents: Subject<any> = new Subject<any>();
    public _deviceSlotUpdateEvents: Subject<any> = new Subject<any>();

    private _devices: {[s: string /* DeviceId*/]: Device}; //convenience-access object for all devices, makes certain use-cases much more efficient
    private _devicesForProjectLoaded: {[s: string /* ProjectId*/]: boolean};

    private _mobileAccessTokensForUser$ = new ReplaySubject<Map<MobileAccessTokenId, MobileAccessToken>>();

    constructor( private eventsService: EventsService, private deviceApi: DeviceApi, private userService: UserService) {
        this._cleanCaches();
    }

    /** signals COMPLETE for all Observables, clears all data */
    private _cleanCaches() {
        if (this._allDeviceIds !== undefined) {
            for (let d$ of valuesOfObject(this._devicesById$)) {
                d$.complete();
            }
            this._allDeviceIds.complete();
        }
        this._devicesForProjectLoaded = {};
        this._devicesById$ = {};
        this._devices = {}; //did cache Objects directly...

        //do not clean the eventsHandle should it exist! (TODO: this is kind of a semantic issue)

        this._allDeviceIds = new ReplaySubject(1);
    }

    /** Simple helper-method to avoid any confusion on why which cache is queried when. This is the central deciding factor if a device is considered cached! */
    private _isDeviceCached(deviceId: DeviceId): boolean {
        return this._devicesById$[deviceId] !== undefined;
    }

    /** Simple helper-method to avoid ever overwriting an already existing ReplaySubject for a Device! */
    private _initDeviceCacheForId(deviceId: DeviceId): void {
        if(this._devicesById$[deviceId] === undefined) this._devicesById$[deviceId] = new ReplaySubject(1)
    }

    private deviceAddedSubscription: Subscription;
    private deviceUpdatedSubscription: Subscription;

    /** the central event handler, makes changes (aka produces new NEXT values) in your Caches depending on the type of Event */
    private static _handleEvents(e: WebmateWebsocketEvent, self: DeviceService, forProject: ProjectId): void {
        if (e === undefined) return;

        if (e.eventType === "Device.Added") {
            if (e.deviceId !== undefined) {
                if (isDefined(self.deviceAddedSubscription)) self.deviceAddedSubscription.unsubscribe();
                self.deviceAddedSubscription = self._queryBackendForDevice(e.deviceId).subscribe();
            }

        } else if (e.eventType === "Device.Updated" || e.eventType === "Device.PropertiesChanged") { //TODO for now we query the device again on property updates for simplicity
            if (e.deviceId !== undefined) { //we have to overwrite the value in the cache, therefore we need to request the most recent data from the backend
                if (isDefined(self.deviceUpdatedSubscription)) self.deviceUpdatedSubscription.unsubscribe();
                self.deviceUpdatedSubscription = self.deviceApi.getDeviceById(e.deviceId).pipe(map(next => {
                    self._overwriteDeviceInCache(next);
                })).subscribe();
            }
        } else if (e.eventType === "Device.Removed") {
            if (self._isDeviceCached(e.deviceId)) {
                //signal no more updates for this Device and clear it from the main Cache
                self._devicesById$[e.deviceId].complete();
                delete self._devicesById$[e.deviceId];
                delete self._devices[e.deviceId];

                //signal removal of this device (works because we have deleted the key above)
                self._updateAllDeviceIds();
            }
        } else if (e.eventType === "Device.AppInstalled") {
            self._appInstalledEvents.next(e);
        } else if (e.eventType === "Device.AppInstallFailed") {
            self._appInstalledEvents.next(e);
        } else if (e.eventType === 'Device.RevertedToInitial') {
            self._deviceRevertedToInitialEvents.next(e);
        } else if (e.eventType === 'DeviceSlot.Updated') {
            self._deviceSlotUpdateEvents.next(e);
        }
        /* else, we do not need to do anything. After all, we had no clue about this device in the first place OR its a completely different Event*/
    }

    /** function that is called when the Events-Service signals that there will be no more Device-Events */
    private static _handleNoMoreEvents(instanceOfThis: any, forProject: ProjectId): void {
        console.warn("DeviceService cleaning Cache since Events-Connection was terminated");
        instanceOfThis.eventsHandle[forProject] = undefined;
        instanceOfThis._cleanCaches();
    }

    public handleAppInstalledEvent(): Observable<any> {
        return this._appInstalledEvents.asObservable();
    }

    public handleDeviceRevertedToInitialEvent(): Observable<any> {
        return this._deviceRevertedToInitialEvents.asObservable();
    }

    public handleDeviceSlotUpdateEvents(): Observable<any> {
        return this._deviceSlotUpdateEvents.asObservable();
    }

    /** updates the DeviceId Cache by setting it to the current value of the main cache. Supports manually adding a single DeviceId,
     *  which comes in handy when receiving certain Events! Note that in this case, you MUST also provide the Project! */
    private _updateAllDeviceIds(): void {
        this._allDeviceIds.next(Object.keys(this._devicesById$ /* could also be this._devices */));
    }

    /** helper-method to warp the complex API-Call that involves busy the BackEnd for a Device and storing it in the Cache */
    private _queryBackendForDevice(deviceId: DeviceId): Observable<Device> {
        return this.deviceApi.getDeviceById(deviceId).pipe(tap(device => {
            this._initDeviceCacheForId(deviceId);
            this._enrichAndStoreInCache(device, false)
        }));
    }

    /** enriches a Device and stores it in the Cache, this method ASSUMES THAT THE SUBJECT EXISTS! */
    private _enrichAndStoreInCache(d: Device, deviceAlreadyKnown: boolean): void {
        let device: Device = deviceFromBackendObject(d);
        this._devicesById$[device.id].next(device);
        this._devices[device.id] = device;
        if (!deviceAlreadyKnown) { this._updateAllDeviceIds(); /* avoid unnecessary updates */}
    }

    /** does what the name says, should the Device not be present it is instead stored anew and the corresponding NEXTs are being fired! Method returns 'true' if cache was
     * overwritten, false if not and we had to start the whole 'cache-it-for-the-first-time' thing instead. This is useful for employing the method in API-Guards' ACCEPTs. */
    private _overwriteDeviceInCache(d: Device): boolean {
        let device: Device = deviceFromBackendObject(d);
        if (this._devicesById$[device.id] !== undefined){
            this._devicesById$[device.id].next(device);
            this._devices[device.id] = device;
            return true;
        } else {
            this._initDeviceCacheForId(device.id);
            this._enrichAndStoreInCache(device, false);
            return false;
        }
    }

    private _registerEventsForProject(projectId: ProjectId): void {
        if (isUUID(projectId) && this._deviceEventsHandleByProject[projectId] === undefined) {
            this._deviceEventsHandleByProject[projectId] = this.eventsService.getEventsForProject(projectId); //register events!
            this._deviceEventsHandleByProject[projectId].subscribe(
                next => DeviceService._handleEvents(next, this, projectId),
                err => console.warn("DeviceService - Events - should never happen!", err /* should never happen, WebSocketService should protect us! */),
                () => DeviceService._handleNoMoreEvents(this, projectId)
            );
        }
    }

    /** boring filtering-helper method */
    private _getDeviceIdsByProject(projectId: ProjectId): DeviceId[]{
        let result = [];
        for (let deviceId in this._devices){
            if (this._devices[deviceId].projectId == projectId) result.push(deviceId);
        }
        return result;
    }


    //   _____ ______ _______      _______ _____ ______             _____ _____
    //  / ____|  ____|  __ \ \    / /_   _/ ____|  ____|      /\   |  __ \_   _|
    // | (___ | |__  | |__) \ \  / /  | || |    | |__        /  \  | |__) || |
    //  \___ \|  __| |  _  / \ \/ /   | || |    |  __|      / /\ \ |  ___/ | |
    //  ____) | |____| | \ \  \  /   _| || |____| |____    / ____ \| |    _| |_
    // |_____/|______|_|  \_\  \/   |_____\_____|______|  /_/    \_\_|   |_____|



    /** returns a new Observable for a specific Devices, either from the cache or from the BackEnd if the cache was empty. */
    getDevice$(deviceId: DeviceId): Observable<Device> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'getDevice$' called with an illegal ID: '"+deviceId+"'");
        if (this._isDeviceCached(deviceId)){
            return this._devicesById$[deviceId].asObservable();
        } else {
            return this.userService.getCurrentUserData().pipe(
                mergeMap(weAreAuthenticated => this._queryBackendForDevice(deviceId)),
                mergeMap( afterwards => this._devicesById$[deviceId].asObservable()),
                tap(device => {
                    // we are also interested in events for a single device
                    this._registerEventsForProject(device.projectId);
                })
            );
        }
    }

    /** returns a new Observable listing all DeviceIds for a specific project, useful for those callers who are only interested in knowing which Devices exist.
     *  *NOTE*: This method does get and cache full Devices, not only DeviceIds from the BackEnd no matter of what is returned. This A) saves code because we
     *  can reuse functionality and B) saves requests since the likelihood of subsequent requests to actually obtain the Device-Data for the Ids is high. */
    getDeviceIdsForProject$(projectId: ProjectId): Observable<DeviceId[]> {
        if (isNoUUID(projectId)) throw new ReferenceError("'getDeviceIdsForProject$' called with an illegal ID: '" + projectId + "'");

        if (this._devicesForProjectLoaded[projectId] !== undefined && this._devicesForProjectLoaded[projectId]) {
            return this._allDeviceIds.pipe(map(deviceIds => this._getDeviceIdsByProject(projectId)));
        } else {
            this._registerEventsForProject(projectId); //we need to listen to events if we do not do so already (can handle the case that we are not yet authenticated)
              //our API-Call gives us whole Devices for efficiency reasons, we will only make one call instead of many!
            return this.deviceApi.getDevicesForProject(projectId).pipe(map(devicesFromBackend => {
                //create new Observables for devices we did not know about until know and cache them!
                if (devicesFromBackend.length > 0){
                    for (let d of devicesFromBackend) {
                        let deviceAlreadyKnown = this._isDeviceCached(d.id);
                        if (!deviceAlreadyKnown){ this._initDeviceCacheForId(d.id); /* never query again, creating the ReplaySubject here ensures this! */}
                        this._enrichAndStoreInCache(d, deviceAlreadyKnown); //if we have up to date information, we may as well store it!
                    }
                } else {
                    //init the convenience-access object, there were no devices for this project
                    this._updateAllDeviceIds(); //
                }
                this._devicesForProjectLoaded[projectId] = true;
            }), mergeMap( afterwards => {
                /* this flatMap is VERY important, if we would directly return 'this._getDeviceIdsByProject(projectId)' and map instead of flatMap, we
                 * would not have the connection to our allDeviceIds-Observable which would mean that the result of this function would NEVER be updated! */
                return this._allDeviceIds.pipe(map(deviceIds => this._getDeviceIdsByProject(projectId)));
            }))
        }
    }

    /** returns a new Observable listing all Devices for a specific Project */
    getDevicesForProject$(projectId: ProjectId): Observable<Device[]> {
        if (isNoUUID(projectId)) throw new ReferenceError("'getDevicesForProject$' called with an illegal ID: '"+projectId+"'");
        return this.getDeviceIdsForProject$(projectId).pipe(mergeMap( listOfDeviceIds => {
            let tmp: DeviceId[] = listOfDeviceIds;
            let tmp2: Observable<Device>[] = tmp.map(deviceId => this.getDevice$(deviceId));
            return tmp2.length == 0 ? observableOf([]) : observableCombineLatest(tmp2).pipe(map(arr => arr));
        }))
    }

    /** requests Deployment of a Device */
    requestDeployment$(projectId: ProjectId, requirements: DeviceRequirements, nameForDevice: string): Observable<Device> {
        if (isNoUUID(projectId)) throw new ReferenceError("'requestDeployment$' called with illegal IDs");
        return this.deviceApi.requestDeploymentInProject(projectId, requirements, nameForDevice);
    }

    /** requests Release of a Device */
    requestReleaseOfDevice$(deviceId: DeviceId): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestReleaseOfDevice$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.requestReleaseOfDevice(deviceId).pipe(map(success => true, error => false));
    }

    /** requests Reconnect of a Device */
    requestReconnectOfDevice$(deviceId: DeviceId): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestReconnectOfDevice$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.requestReconnectOfDevice(deviceId).pipe(map(success => true, error => false));
    }

    /** requests Reconnect of a Device */
    requestResetOfDevice$(deviceId: DeviceId): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestResetOfDevice$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.requestResetOfDevice(deviceId).pipe(map(success => true, error => false));
    }

    /** reports malfunction of a Device */
    sendMalfunctionReportOfDevice$(deviceId: DeviceId, title: string, message: string, projectId: ProjectId): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'sendMalfunctionReportOfDevice$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.sendMalfunctionReportOfDevice(deviceId, title, message, projectId).pipe(map(success => true, error => false));
    }

    requestDeviceUpdate(slotId: DeviceSlotId, openSTFSerial: string, currentVersion: string, message: string, projectId: ProjectId): Observable<GenericApiResponse> {
        return this.deviceApi.requestDeviceUpdate(slotId, openSTFSerial, currentVersion, message, projectId);
    }

    /** requests Rename of a Device */
    requestRenameOfDevice$(deviceId: DeviceId, name: string): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestRenameOfDevice$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.requestRenameOfDevice(deviceId, name).pipe(map(success => true, error => false));
    }

    /** responsible for switching between two binary TicketCapacity States */
    requestVisibilityToJobsChange$(deviceId: DeviceId, newValue: boolean): Observable<GenericApiResponse> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestVisibleToJobsChange$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.imposeDevicePropertyRequirement(
            deviceId,
            DevicePropertyNames.AutomationAvailable,
            newValue
        );
    }

    requestSnapshotOfDevice$(deviceId: DeviceId): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestSnapshotOfDevice$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.requestSnapshotOfDevice(deviceId).pipe(map(success => true, error => false ));
    }

    requestRevertToSnapshot$(deviceId: DeviceId): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestRevertToSnapshot$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.requestRevertToSnapshot(deviceId).pipe(map(success => true, error => false ));
    }

    requestRevertToInitial$(deviceId: DeviceId): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestRevertToInitial$' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.requestRevertToInitial(deviceId).pipe(map(success => true, error => false ));
    }

    requestRedeployment(deviceId: DeviceId): Observable<boolean> {
        if (isNoUUID(deviceId)) throw new ReferenceError("'requestRedeployment' called with an illegal ID: '" + deviceId + "'");
        return this.deviceApi.requestRedeployment(deviceId).pipe(map(success => true, error => false ));
    }

    setAppInstallRequirement(deviceId: DeviceId, virtualPackage: VirtualPackage, instrumented: boolean): Observable<void> {
        const packageInstallation = createFinishedPackageInstallation(virtualPackage, instrumented);
        // Making the API call triggers events, e.g. DeviceAppInstalled
        this._registerEventsForProject(virtualPackage.projectId);
        return this.deviceApi.setAppInstallRequirement(deviceId, packageInstallation).pipe(map(success => {}));
    }

    /**
     * Requests the installation of an App on a Device and emits once the Device reports the outcome. The Event-Subscription is established *before* the API
     * call is made, otherwise a fast installation could emit its Event before anyone listens and the caller would wait forever (the Events are handed out via
     * a plain Subject, there is nothing to replay). Fails with a TimeoutError if the Device does not report back in time, so callers can always give feedback.
     */
    installAppOnDevice$(deviceId: DeviceId, virtualPackage: VirtualPackage, instrumented: boolean,
                        timeoutMs: number = DeviceService.APP_INSTALLATION_TIMEOUT_MS): Observable<AppInstallationResult> {
        const installationReported$: Observable<AppInstallationResult> = this.handleAppInstalledEvent().pipe(
            filter(evt => evt.deviceId === deviceId),
            take(1),
            timeout(timeoutMs)
        );
        const requestInstallation$ = this.setAppInstallRequirement(deviceId, virtualPackage, instrumented).pipe(ignoreElements());
        return merge(installationReported$, requestInstallation$);
    }

    setBandwidthLimit(deviceId: DeviceId, speed: string): Observable<GenericApiResponse> {
        return this.deviceApi.setDeviceBandwidth(deviceId, speed);
    }

    setPacketLoss(deviceId: DeviceId, loss: string): Observable<GenericApiResponse> {
        return this.deviceApi.setSlotPacketLoss(deviceId, loss);
    }

    setDisconnected(deviceId: DeviceId, disconnected: boolean): Observable<GenericApiResponse> {
        return this.deviceApi.setDisconnected(deviceId, disconnected);
    }

    getBrowserSessionsForDevice(deviceId: DeviceId): Observable<Array<BrowserSessionInfo>> {
        return this.deviceApi.getBrowserSessionsForDevice(deviceId)
            .pipe(map(dtos => dtos.map(dto =>BrowserSessionInfo.fromBackendObject(dto))));
    }

    /////// Mobile Access /////
    observeMobileAccessTokensForUser$(): Observable<Map<MobileAccessTokenId, MobileAccessToken>> {
        return this._mobileAccessTokensForUser$
            .asObservable()
            .pipe(distinctUntilChanged());
    }

    createMobileAccessToken(description: string, expirationTime: DateTime | undefined): Observable<MobileAccessTokenId> {
        return this.userService.getCurrentUserData().pipe(
            mergeMap(user => this.deviceApi.createMobileAccessToken(description, user.id, expirationTime)));
    }

    fetchMobileAccessTokensForUser() {
        return this.userService.getCurrentUserData().pipe(mergeMap(user => {
            return this.deviceApi.fetchMobileAccessTokensForUser(user.id);
        }), map(tokens => {
            const resultList = tokens ?? [];
            const result = new Map<MobileAccessTokenId, MobileAccessToken>();
            resultList.forEach(token => {
                if (!result.has(token.id)) {
                    result.set(token.id, token);
                }
            });
            this._mobileAccessTokensForUser$.next(result);
            return resultList;
        }), take(1)).subscribe();
    }

    fetchMobileAccessTokensForCustomer$(): Observable<MobileAccessToken[]> {
        return this.deviceApi.fetchMobileAccessTokensForCustomer().pipe(map(res => res ?? []));
    }

    deleteOrRevokeMobileAccessToken(id: MobileAccessTokenId): Observable<GenericApiResponse> {
        return this.deviceApi.deleteOrRevokeMobileAccessToken(id);
    }

    imposeDevicePropertyRequirement(deviceId: DeviceId, propertyName: string, requirement: any): Observable<void> {
        return this.deviceApi.imposeDevicePropertyRequirement(deviceId, propertyName, requirement).pipe(map(success => {}));
    }

    imposeDevicePropertyRequirements(deviceId: DeviceId, updates: DevicePropertyRequirementUpdate[]): Observable<Device> {
        if (updates.length === 0) {
            return this.getDevice$(deviceId).pipe(take(1));
        }

        const expectedProperties = updates.reduce((acc, update) => {
            acc[update.propertyName] = update.value;
            return acc;
        }, {} as Partial<DeviceProperties>);

        return from(updates).pipe(
            concatMap(update => this.deviceApi.imposeDevicePropertyRequirement(deviceId, update.propertyName, update.value)),
            last(),
            switchMap(() => this.waitForDeviceProperties$(deviceId, expectedProperties))
        );
    }

    waitForDeviceProperties$(deviceId: DeviceId, expectedProperties: Partial<DeviceProperties>): Observable<Device> {
        return this.getDevice$(deviceId).pipe(
            filter(device => this.matchesExpectedProperties(device.properties, expectedProperties)),
            take(1)
        );
    }

    private matchesExpectedProperties(actualProperties: DeviceProperties, expectedProperties: Partial<DeviceProperties>): boolean {
        return Object.keys(expectedProperties).every(propertyName =>
            this.matchesExpectedValue(actualProperties?.[propertyName], expectedProperties[propertyName])
        );
    }

    private matchesExpectedValue(actualValue: any, expectedValue: any): boolean {
        if (Array.isArray(expectedValue)) {
            if (!Array.isArray(actualValue) || actualValue.length !== expectedValue.length) {
                return false;
            }
            return expectedValue.every((value, index) => this.matchesExpectedValue(actualValue[index], value));
        }

        if (expectedValue !== null && typeof expectedValue === 'object') {
            if (actualValue === null || actualValue === undefined || typeof actualValue !== 'object') {
                return false;
            }
            return Object.keys(expectedValue).every(key => this.matchesExpectedValue(actualValue[key], expectedValue[key]));
        }

        return actualValue === expectedValue;
    }

    getDeviceAudioWebRtcSignalingURL(deviceId: DeviceId): string {
        return this.deviceApi.getDeviceAudioWebRtcSignalingURL(deviceId);
    }

    takeDeviceScreenshot(deviceId: DeviceId): Observable<string> {
        return this.deviceApi.takeDeviceScreenshot(deviceId);
    }

    /**
     * Get DevTools endpoints available at the given device.
     */
    getDeviceDevToolsEndpoints(deviceId: DeviceId): Observable<DevToolsEndpoint[]> {
        return this.deviceApi.getDeviceDevToolsEndpoints(deviceId);
    }

    getDeviceDevToolsEndpointTargets(deviceId: DeviceId, endpointId: string): Observable<any[]> {
        return this.deviceApi.getDeviceDevToolsEndpointTargets(deviceId, endpointId);
    }

    makeDeviceDevToolsUrl(device: Device, endpointId: string, targetId: string, frontendType: 'chromium' | 'webkit'): string {
        if (frontendType === 'webkit') {
            return this.deviceApi.makeWebkitDevToolsUrl(device.id, endpointId, targetId, device.platform.platformVersion);
        } else {
            return this.deviceApi.makeChromiumDevToolsUrl(device.id, endpointId, targetId);
        }
    }

}
